import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setToken, getToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, obj = auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const { data } = await api.post("/auth/google", { credential });
      // 2-step SSO: brand-new email returns `pending_signup` without
      // an access_token — the caller must render the role-picker UI
      // and complete via `completeGoogleSignup(token, role)`.
      if (data.pending_signup) {
        return {
          ok: true,
          pending_signup: true,
          pending_signup_token: data.pending_signup_token,
          profile: data.profile,
        };
      }
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user, new_user: data.new_user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const completeGoogleSignup = async (pendingToken, role) => {
    try {
      const { data } = await api.post("/auth/google/complete-signup", {
        pending_signup_token: pendingToken,
        role,
      });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user, new_user: data.new_user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // ── Passwordless Email OTP login ──
  const requestEmailLoginOtp = async (email) => {
    try {
      const { data } = await api.post("/auth/email-login-otp/request", { email });
      // Server always returns { ok: true } regardless of whether the
      // email is known (anti-enumeration).  In mock-email environments
      // `mock_otp` is included for preview convenience.
      return {
        ok: true,
        throttled: !!data?.throttled,
        mock_otp: data?.mock_otp || null,
      };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const verifyEmailLoginOtp = async (email, otp) => {
    try {
      const { data } = await api.post("/auth/email-login-otp/verify", { email, otp });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* best-effort — server-side cookie clears regardless */ }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, completeGoogleSignup, register, logout, refresh, requestEmailLoginOtp, verifyEmailLoginOtp }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
